import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@frontegg/react";
import { useApolloClient, useMutation } from "@apollo/client";
import {
  ConfirmDialog,
  FileUpload,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@outpave/ds";
import axios from "axios";
import { toast } from "react-toastify";
import { getImpersonationFromStorage } from "contexts/ImpersonationContext";
import { REMOVE_USER_PROFILE_PICTURE } from "graphql/user";
import httpClient from "utils/httpClient";
import { fullName } from "utils/fullName";
import UserAvatar, { type UserAvatarUser } from "./UserAvatar";

const PICTURE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
// The server's cap, checked here so a large photo fails before it uploads.
const MAX_PICTURE_BYTES = 25 * 1024 * 1024;

interface ProfilePictureMenuProps {
  user: UserAvatarUser & { id: string };
  ownRecord: boolean;
  isAdminOrHigher: boolean;
}

/**
 * The profile avatar, opening its picture actions when there are any. Only the
 * person uploads their own picture; an admin can remove anyone's in the
 * company, which is moderation, not a way to set someone's picture.
 */
const ProfilePictureMenu: React.FC<ProfilePictureMenuProps> = ({
  user,
  ownRecord,
  isAdminOrHigher,
}) => {
  const { t } = useTranslation();
  const { user: authUser } = useAuth();
  const { cache } = useApolloClient();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removePicture, { loading: removing }] = useMutation(
    REMOVE_USER_PROFILE_PICTURE,
  );
  const hasPicture = !!user.profilePicUrl;
  // The upload acts as the token's owner, so while impersonating it would change the admin's own picture.
  const canUpload = ownRecord && !getImpersonationFromStorage();
  const canRemove = hasPicture && (ownRecord || isAdminOrHigher);

  const avatar = <UserAvatar size="lg" user={user} />;
  if (!canUpload && !canRemove) return avatar;

  const upload = async (file: File | undefined) => {
    if (!file) return;
    if (!PICTURE_TYPES.includes(file.type)) {
      toast.error(t("Upload a JPEG, PNG, WebP or GIF image"));
      return;
    }
    if (file.size > MAX_PICTURE_BYTES) {
      toast.error(t("Profile picture is too large"));
      return;
    }
    const formData = new FormData();
    formData.append("profilePicture", file);
    setUploading(true);
    try {
      const res = await httpClient.post("/users/update-profile", formData, {
        headers: { Authorization: `Bearer ${authUser?.accessToken}` },
      });
      // A REST upload never reaches Apollo, so write the new URL where every avatar of this person reads it.
      cache.modify({
        id: cache.identify({ __typename: "User", id: user.id }),
        fields: { profilePicUrl: () => res.data?.profilePicUrl ?? null },
      });
    } catch (error) {
      // The server's refusals are translation keys, such as a photo too large to process.
      const refusal =
        axios.isAxiosError(error) &&
        error.response?.status === 400 &&
        typeof error.response.data === "string"
          ? error.response.data
          : "Failed to update profile";
      toast.error(t(refusal, { nsSeparator: false, keySeparator: false }));
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    try {
      await removePicture({ variables: { id: user.id } });
      setConfirmingRemove(false);
    } catch {
      toast.error(t("Failed to update profile"));
    }
  };

  const label = canUpload
    ? t("Change profile picture")
    : t("Remove profile picture");
  const closeMenu = () => setMenuAnchor(null);

  const renderMenu = (openPicker?: () => void) => (
    <>
      {/* A disabled button fires no events, so the tooltip wraps it in a span and describes rather than names it. */}
      <Tooltip title={label} describeChild>
        <span>
          <IconButton
            aria-label={label}
            variant="ghost"
            size="small"
            disabled={uploading}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
          >
            {avatar}
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        {openPicker && (
          <MenuItem
            onClick={() => {
              closeMenu();
              openPicker();
            }}
          >
            {hasPicture ? t("Change picture") : t("Upload picture")}
          </MenuItem>
        )}
        {canRemove && (
          <MenuItem
            danger
            onClick={() => {
              closeMenu();
              setConfirmingRemove(true);
            }}
          >
            {t("Remove picture")}
          </MenuItem>
        )}
      </Menu>
    </>
  );

  return (
    <>
      {canUpload ? (
        <FileUpload
          variant="trigger"
          accept={PICTURE_TYPES.join(",")}
          disabled={uploading}
          onFiles={(files) => upload(files[0])}
        >
          {renderMenu}
        </FileUpload>
      ) : (
        renderMenu()
      )}
      <ConfirmDialog
        open={confirmingRemove}
        tone="danger"
        title={t("Remove profile picture")}
        message={
          ownRecord
            ? t("Remove your profile picture?")
            : t("RemoveUserProfilePicture", { name: fullName(user) })
        }
        confirmLabel={t("Remove")}
        busy={removing}
        onConfirm={remove}
        onCancel={() => setConfirmingRemove(false)}
      />
    </>
  );
};

export default ProfilePictureMenu;
